import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneRight, Clock } from "@phosphor-icons/react";

const STATUS_TONE = {
  open: "bg-blue-50 border-blue-300 text-blue-700",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  resolved: "bg-emerald-50 border-emerald-300 text-emerald-700",
  closed: "bg-zinc-100 border-zinc-300 text-zinc-600",
};

export default function TicketDetail() {
  const { tid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [msg, setMsg] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/tickets/${tid}`);
      setTicket(r.data);
    } catch (e) {
      toast.error("Ticket not found");
      navigate("/app/tickets");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);

  const send = async () => {
    if (!msg.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tickets/${tid}/messages`, { body: msg });
      setMsg("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/tickets/${tid}`, { status });
      toast.success(`Ticket ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!ticket) return <div className="text-sm text-zinc-500">Loading…</div>;

  const isStaff = ["institution_admin", "super_admin", "examiner", "proctor"].includes(user?.role);
  const isOwner = ticket.user_id === user?.user_id;

  return (
    <div>
      <Link to="/app/tickets" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mb-4">
        <ArrowLeft size={12} /> Back to help desk
      </Link>

      <div className="border border-zinc-200 p-8 mb-6" data-testid="ticket-detail">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 ${STATUS_TONE[ticket.status]}`}>{ticket.status?.replace("_", " ")}</span>
              <span className="text-[10px] uppercase tracking-wider border border-zinc-300 px-2 py-0.5">{ticket.category}</span>
              <span className="text-[10px] uppercase tracking-wider border border-zinc-300 px-2 py-0.5">{ticket.priority}</span>
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="ticket-subject">{ticket.subject}</h1>
            <div className="text-xs text-zinc-500 mt-2">
              Opened by <span className="text-zinc-900 font-medium">{ticket.user_name}</span> ({ticket.user_email}) · <span className="inline-flex items-center gap-1"><Clock size={11} /> {new Date(ticket.created_at).toLocaleString()}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {isStaff && ticket.status !== "resolved" && (
              <button onClick={() => updateStatus("resolved")} className="text-xs border border-emerald-500 text-emerald-700 px-3 py-2 hover:bg-emerald-50" data-testid="ticket-resolve-btn">
                Mark resolved
              </button>
            )}
            {(isOwner || isStaff) && ticket.status !== "closed" && (
              <button onClick={() => updateStatus("closed")} className="text-xs border border-zinc-400 text-zinc-700 px-3 py-2 hover:bg-zinc-50" data-testid="ticket-close-btn">
                Close
              </button>
            )}
            {(isOwner || isStaff) && ticket.status === "closed" && (
              <button onClick={() => updateStatus("open")} className="text-xs border border-blue-500 text-blue-700 px-3 py-2 hover:bg-blue-50" data-testid="ticket-reopen-btn">
                Reopen
              </button>
            )}
          </div>
        </div>
        <div className="text-sm text-zinc-800 whitespace-pre-wrap mt-6" data-testid="ticket-body">
          {ticket.body}
        </div>
      </div>

      {/* Messages */}
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-3">
          Conversation ({ticket.message_count || 0})
        </div>
        <div className="space-y-3" data-testid="ticket-messages">
          {(ticket.messages || []).map((m) => {
            const isSelf = m.author_id === user?.user_id;
            return (
              <div key={m.message_id} className={`border p-4 ${isSelf ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`} data-testid={`ticket-message-${m.message_id}`}>
                <div className="flex items-center gap-3 mb-2 text-xs">
                  <span className="text-zinc-900 font-medium">{m.author_name}</span>
                  <span className="uppercase tracking-wider text-[10px] border border-zinc-300 px-2 py-0.5">{m.author_role?.replace("_", " ")}</span>
                  <span className="text-zinc-500">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{m.body}</div>
              </div>
            );
          })}
          {(!ticket.messages || ticket.messages.length === 0) && (
            <div className="border border-dashed border-zinc-200 p-6 text-sm text-zinc-500 text-center">
              No messages yet — start the conversation below.
            </div>
          )}
        </div>
      </div>

      {ticket.status !== "closed" && (
        <div className="border border-zinc-200 p-5" data-testid="ticket-reply-form">
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={3}
            placeholder="Type your message…"
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
            data-testid="ticket-message-input"
          />
          <div className="flex justify-end">
            <button
              onClick={send}
              disabled={posting || !msg.trim()}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="ticket-message-send-btn"
            >
              <PaperPlaneRight size={13} weight="fill" /> {posting ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
